/**
 * AccessError - Error display for portal access validation
 * Nordic Sanctuary design: Clear, helpful, non-alarming
 */

import { useTranslation } from "react-i18next";
import { AlertCircle, Clock, Lock, HelpCircle } from "lucide-react";

type ErrorType = "invalid" | "locked_temp" | "locked_perm" | "expired" | "not_found" | "rate_limited";

interface AccessErrorProps {
  type: ErrorType;
  remainingAttempts?: number;
  unlockAt?: number;
  retryAfter?: number;
}

const iconMap: Record<ErrorType, typeof AlertCircle> = {
  invalid: AlertCircle,
  locked_temp: Clock,
  locked_perm: Lock,
  expired: Clock,
  not_found: HelpCircle,
  rate_limited: Clock,
};

const severityMap: Record<ErrorType, "warning" | "error" | "info"> = {
  invalid: "warning",
  locked_temp: "warning",
  locked_perm: "error",
  expired: "info",
  not_found: "info",
  rate_limited: "warning",
};

const severityStyles = {
  warning: {
    container: "bg-[oklch(0.98_0.03_70)] border-[oklch(0.85_0.10_70)]",
    icon: "text-[oklch(0.65_0.15_70)]",
    title: "text-[oklch(0.45_0.10_70)]",
    text: "text-[oklch(0.40_0.05_70)]",
  },
  error: {
    container: "bg-[oklch(0.97_0.02_25)] border-[oklch(0.85_0.10_25)]",
    icon: "text-[oklch(0.55_0.20_25)]",
    title: "text-[oklch(0.45_0.15_25)]",
    text: "text-[oklch(0.40_0.08_25)]",
  },
  info: {
    container: "bg-[var(--stone-50)] border-[var(--stone-200)]",
    icon: "text-[var(--stone-500)]",
    title: "text-[var(--stone-700)]",
    text: "text-[var(--stone-600)]",
  },
};

function getMessage(
  t: (key: string, options?: Record<string, unknown>) => string,
  type: ErrorType,
  remainingAttempts?: number,
  unlockAt?: number,
  retryAfter?: number
): string {
  switch (type) {
    case "invalid":
      if (remainingAttempts !== undefined && remainingAttempts > 0) {
        return t("errors.invalid.message", { count: remainingAttempts });
      }
      return t("errors.invalid.messageFallback");
    case "locked_temp":
      if (unlockAt) {
        const unlockDate = new Date(unlockAt);
        const minutes = Math.ceil((unlockDate.getTime() - Date.now()) / 60000);
        if (minutes > 0) {
          return t("errors.lockedTemp.message", { count: minutes });
        }
      }
      return t("errors.lockedTemp.messageFallback");
    case "locked_perm":
      return t("errors.lockedPerm.message");
    case "expired":
      return t("errors.expired.message");
    case "not_found":
      return t("errors.notFound.message");
    case "rate_limited":
      if (retryAfter && retryAfter > 0) {
        const minutes = Math.ceil(retryAfter / 60);
        return t("errors.rateLimited.message", { count: minutes });
      }
      return t("errors.rateLimited.messageFallback");
    default:
      return t("errors.invalid.messageFallback");
  }
}

function getTitle(
  t: (key: string) => string,
  type: ErrorType
): string {
  switch (type) {
    case "invalid":
      return t("errors.invalid.title");
    case "locked_temp":
      return t("errors.lockedTemp.title");
    case "locked_perm":
      return t("errors.lockedPerm.title");
    case "expired":
      return t("errors.expired.title");
    case "not_found":
      return t("errors.notFound.title");
    case "rate_limited":
      return t("errors.rateLimited.title");
    default:
      return t("errors.invalid.title");
  }
}

export function AccessError({
  type,
  remainingAttempts,
  unlockAt,
  retryAfter,
}: AccessErrorProps) {
  const { t } = useTranslation("portalAccess");
  const severity = severityMap[type];
  const styles = severityStyles[severity];
  const Icon = iconMap[type];
  const title = getTitle(t, type);
  const message = getMessage(t, type, remainingAttempts, unlockAt, retryAfter);

  return (
    <div
      className={`rounded-xl border-2 p-4 ${styles.container}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex gap-3">
        <div className={`flex-shrink-0 mt-0.5 ${styles.icon}`}>
          <Icon className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-base mb-1 ${styles.title}`}>
            {title}
          </h3>
          <p className={`text-sm leading-relaxed ${styles.text}`}>{message}</p>
        </div>
      </div>
    </div>
  );
}
